"use client";
import { useMemo, useRef } from "react";
import { useAppStore } from "@/store/app";
import { useDataStore } from "@/store/data";
import { cn, getLocationStateFips, getValue } from "@/utils/helpers";
import { getIndividualYears, getLang, getUnit, isStatCaOnly } from "@/utils/selectors";
import { ControlsStatType, HoveredLocationProps, LocationProps } from "@/utils/types";
import Chart from "@/components/common/Chart";
import Loader from "@/components/common/Loader";
import styles from "../Profile.module.css";

const years = [...getIndividualYears()].reverse();

export interface ProfileSmokeChartProps {
	stat: ControlsStatType;
	location?: LocationProps | HoveredLocationProps | null;
}

export default function ProfileSmokeChart({
	stat,
	location
} : ProfileSmokeChartProps) {
	const ref = useRef(null);
	const controls = useAppStore(store => store.controls);
	const dataLoading = useDataStore(store => store.dataLoading);

	// If is CA-specific stat but not in CA
	const hasNoData = useMemo(() =>
		!location || getLocationStateFips(location.id) !== "06" && isStatCaOnly(stat)
	, [stat, location]);

	const chartUnit = getUnit(stat);

	const chartData = useMemo(() => {
		return years.map(year => {
			const key = `smoke_${stat}_${year}`;
			return {
				x: new Date(`January 1, ${year} 00:00:00`),
				y: getValue(key, location)
			};
		}).filter(datum =>
			datum.x instanceof Date && typeof datum.y === "number"
		)
	}, [stat, location, controls]);

	return (
		<div
			ref={ref}
			className={styles.ProfileSmokeChart}
		>
			<Loader
				loading={dataLoading}
			/>
			<div
				className={cn(
					styles.ProfileLoadable,
					dataLoading ? styles.ProfileLoadable_loading : null,
				)}
			>
				{hasNoData ?
					<div
						className={styles.ProfileSmokeMissing}
					>
						<p>{getLang("profile", "missing")}</p>
					</div>
					: <Chart
						data={chartData}
						unit={chartUnit}
						year={Number(`20${controls.year}`)}
						className={styles.ProfileSmokeChartChart}
					/>
				}
			</div>
		</div>
	);
}